import React, { useState } from "react";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
} from "@/components/ui/sidebar";
import {
    Bell,
    ChevronRight,
    FileQuestion,
    Forklift,
    HardHat,
    Settings,
    ShieldAlert,
    Users,
    type LucideIcon,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Link, useNavigate } from "react-router";
import {
    type NavigationRoute,
    routeActionConfiguration,
    routeRobots,
    routeBulkImport,
    routeColumnVisibility,
    routeDashboard,
    routeEloshieldClassicStationaryModules,
    routeGeneralSettings,
    routeImpactManagement,
    routeIncidents,
    routeLoginReport,
    routeMaintenance,
    routeMotionSensors,
    routeOldDashboard,
    routePedestrianDevices,
    routePhones,
    routePreOpsChecklists,
    routePreOpsChecklistSettings,
    routeSafetyMessages,
    routeSafetyMessagesSettings,
    routeSettings,
    routeShockProfiles,
    routeStationaryUnits,
    routeSystemWide,
    routeUserProductivity,
    routeUsers,
    routeUserSettings,
    routeVehicleDevices,
    routeVehicleMovementPeriods,
    routeVehicleProductivity,
    routeVehicles,
    routeVehicleSettings,
    routeAgv,
} from "../../routes";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { store } from "../../store";
import { ManagementRole, Mode } from "../../api";
import { LogoutReason, setLogoutReason } from "../../domain/services/service-auth";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Notifications } from "./notifications";
import { HelpCenter } from "./help-center";
import { type HelpArticle } from "../../help-types";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLocalStorage } from "../../utils/hooks/use-local-storage";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export type NavigationGroupProps = React.PropsWithChildren & {
    header: React.ReactNode;
};

const NavigationItem = ({ route }: { route: NavigationRoute }): JSX.Element => {
    const { t } = useTranslation();
    const isActive = store.historyProvider.isOnRoute(route);

    // If a route is ELOfleet-only and the instance is set to ELOshield mode, disable this route.
    if (route.elofleetOnly && store.servicePublicSettings.publicSetting?.mode === Mode.ELOSHIELD) {
        return (
            <SidebarMenuItem>
                <TooltipProvider delayDuration={150}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="block">
                                <SidebarMenuButton
                                    asChild
                                    aria-disabled="true"
                                    className="cursor-not-allowed opacity-50 hover:bg-transparent hover:text-current"
                                >
                                    <span>
                                        {route.icon && <route.icon className="size-4 shrink-0" />}
                                        <span className="flex-1 truncate">{t(route.title)}</span>
                                    </span>
                                </SidebarMenuButton>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{t("component.sidebarNavigation.disabled")}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </SidebarMenuItem>
        );
    }

    return (
        <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive}>
                <Link to={route.path()}>
                    {route.icon && <route.icon />}
                    {t(route.title)}
                </Link>
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
};

const NavigationGroup = ({
    title,
    icon: Icon,
    routes,
}: {
    title: string;
    icon: LucideIcon;
    routes: NavigationRoute[];
}): JSX.Element => {
    const { t } = useTranslation();
    const isActive = routes.some((route) => store.historyProvider.isOnRoute(route));

    // If all routes inside a navigation group are ELOfleet-only, disable the whole group.
    if (
        routes.every((r) => r.elofleetOnly) &&
        store.servicePublicSettings.publicSetting?.mode === Mode.ELOSHIELD
    ) {
        return (
            <SidebarMenuItem>
                <TooltipProvider delayDuration={150}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="block">
                                <SidebarMenuButton
                                    asChild
                                    aria-disabled="true"
                                    className="cursor-not-allowed opacity-50 hover:bg-transparent hover:text-current"
                                >
                                    <span onClick={(e) => e.preventDefault()}>
                                        <Icon />
                                        {title}
                                        <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                                    </span>
                                </SidebarMenuButton>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{t("component.sidebarNavigation.disabled")}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </SidebarMenuItem>
        );
    }

    return (
        <SidebarMenu>
            <Collapsible className="group/collapsible" defaultOpen={isActive}>
                <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                        <SidebarMenuButton asChild>
                            <span>
                                <Icon />
                                {title}
                                <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                            </span>
                        </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <SidebarMenuSub>
                            {routes.map((r) => (
                                <NavigationItem route={r} key={r.pattern} />
                            ))}
                        </SidebarMenuSub>
                    </CollapsibleContent>
                </SidebarMenuItem>
            </Collapsible>
        </SidebarMenu>
    );
};

export type AppSidebarProps = {
    suggestedHelpArticles?: HelpArticle[];
};

export const AppSidebar = observer(({ suggestedHelpArticles }: AppSidebarProps): JSX.Element => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [helpOpened, setHelpOpened] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);

    // Feature flag
    const agvSectionEnabled = store.serviceAgv.enabled;
    const robotsSectionEnabled = store.serviceRobots.enabled;

    const user = store.serviceOwnUser.user;
    const showAdminMenu = user?.managementRole === ManagementRole.SUPER_ADMIN;
    const showSetupMenu =
        user?.managementRole === ManagementRole.INSTANCE_ADMIN ||
        user?.managementRole === ManagementRole.SUPER_ADMIN;

    const logout = async (): Promise<void> => {
        if (store.serviceLoginToken.loginToken?.context === "sso") {
            setLogoutReason(LogoutReason.USER_LOGOUT);
            const url = await store.loginTokensApi.loginTokensLogoutGet();
            window.location.href = url;
        } else {
            await store.serviceAuth.logout(LogoutReason.USER_LOGOUT);
        }
    };

    const imageUrl = user?.imageId != null ? store.serviceFile.getFileUri(user.imageId) : undefined;
    const initials =
        `${(user?.firstName ?? "")[0]}${(user?.lastName ?? "")[0]}`.toLocaleUpperCase();

    const unreadNotifications = Array.from(
        store.repositoryPersistentNotifications.entities.values(),
    ).filter((notification) => notification.read === false).length;

    const [dashboardIsLegacy, _] = useLocalStorage("legacyDashboard", false);
    return (
        <>
            <Sidebar>
                <SidebarHeader>
                    <a href="/" className="flex justify-center">
                        <img
                            src={store.serviceSidebar.logo}
                            alt="ELOfusion"
                            className="m-2 max-h-8 object-contain"
                        />
                    </a>
                </SidebarHeader>
                {/* Preflight required for portal view (on small screens) to be styled correctly */}
                <SidebarContent className="preflight">
                    <ScrollArea>
                        <SidebarGroup>
                            <SidebarGroupLabel>
                                {t("component.sidebarNavigation.mainMenu")}
                            </SidebarGroupLabel>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    <NavigationItem
                                        route={
                                            dashboardIsLegacy ? routeOldDashboard : routeDashboard
                                        }
                                    />
                                    <NavigationGroup
                                        title={t("component.sidebarNavigation.fleet")}
                                        icon={Forklift}
                                        routes={[
                                            routeVehicles,
                                            routeVehicleProductivity,
                                            routeVehicleMovementPeriods,
                                        ]}
                                    />
                                    <NavigationGroup
                                        title={t("component.sidebarNavigation.driverTeam")}
                                        icon={Users}
                                        routes={[
                                            routeUsers,
                                            routeUserProductivity,
                                            routeLoginReport,
                                        ]}
                                    />
                                    <NavigationGroup
                                        title={t("component.sidebarNavigation.safetyManagement")}
                                        icon={HardHat}
                                        routes={[
                                            routeIncidents,
                                            routeImpactManagement,
                                            routePreOpsChecklists,
                                            routeSafetyMessages,
                                        ]}
                                    />
                                    <NavigationItem route={routeMaintenance} />
                                    {agvSectionEnabled && <NavigationItem route={routeAgv} />}
                                    {robotsSectionEnabled && <NavigationItem route={routeRobots} />}
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                        <SidebarGroup>
                            <SidebarGroupLabel>
                                {t("component.sidebarNavigation.other")}
                            </SidebarGroupLabel>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    {store.servicePublicSettings.publicSetting?.mode ===
                                    Mode.ELOSHIELD ? (
                                        <SidebarMenuItem>
                                            <TooltipProvider delayDuration={150}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="block">
                                                            <SidebarMenuButton
                                                                asChild
                                                                aria-disabled="true"
                                                                className="cursor-not-allowed opacity-50 hover:bg-transparent hover:text-current"
                                                            >
                                                                <span>
                                                                    <Bell />
                                                                    <span>
                                                                        {t(
                                                                            "component.sidebarNavigation.notifications",
                                                                        )}
                                                                    </span>
                                                                </span>
                                                            </SidebarMenuButton>
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {t("component.sidebarNavigation.disabled")}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </SidebarMenuItem>
                                    ) : (
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                asChild
                                                onClick={() => setNotificationsOpen(true)}
                                            >
                                                <span>
                                                    <Bell />
                                                    <span>
                                                        {t(
                                                            "component.sidebarNavigation.notifications",
                                                        )}
                                                    </span>
                                                </span>
                                            </SidebarMenuButton>
                                            {unreadNotifications > 0 && (
                                                <SidebarMenuBadge>
                                                    <div className="bg-primary text-sidebar-accent-foreground h-6 w-6 rounded-full p-1 text-center text-xs font-medium">
                                                        {unreadNotifications}
                                                    </div>
                                                </SidebarMenuBadge>
                                            )}
                                        </SidebarMenuItem>
                                    )}
                                    <SidebarMenuItem>
                                        <SidebarMenuButton
                                            asChild
                                            onClick={() => setHelpOpened(true)}
                                        >
                                            <span>
                                                <FileQuestion />
                                                <span>
                                                    {t("component.sidebarNavigation.helpCenter")}
                                                </span>
                                            </span>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                        {showSetupMenu && (
                            <SidebarGroup>
                                <SidebarGroupLabel>
                                    {t("component.sidebarNavigation.setup")}
                                </SidebarGroupLabel>
                                <SidebarGroupContent>
                                    <NavigationGroup
                                        title={t("component.sidebarNavigation.setup")}
                                        icon={Settings}
                                        routes={[
                                            routeUserSettings,
                                            routeVehicleSettings,
                                            routePreOpsChecklistSettings,
                                            routeSafetyMessagesSettings,
                                            routeSystemWide,
                                        ]}
                                    />
                                    {showAdminMenu && (
                                        <NavigationGroup
                                            title={t("component.sidebarNavigation.settings")}
                                            icon={ShieldAlert}
                                            routes={[
                                                routeGeneralSettings,
                                                routeVehicleDevices,
                                                routeShockProfiles,
                                                routeColumnVisibility,
                                                routePedestrianDevices,
                                                routeStationaryUnits,
                                                routeEloshieldClassicStationaryModules,
                                                routePhones,
                                                routeMotionSensors,
                                                routeBulkImport,
                                                routeActionConfiguration,
                                            ]}
                                        />
                                    )}
                                </SidebarGroupContent>
                            </SidebarGroup>
                        )}
                    </ScrollArea>
                </SidebarContent>
                {/* Preflight required for portal view (on small screens) to be styled correctly */}
                <SidebarFooter className="preflight">
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <SidebarMenuButton size="lg">
                                        <Avatar>
                                            <AvatarImage src={imageUrl} alt="avatar" />
                                            <AvatarFallback>{initials}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <div className="text-sm leading-none font-semibold">
                                                {user?.firstName} {user?.lastName}
                                            </div>
                                            <div className="text-xs">
                                                {store.i18n.formatFleetRole(user?.fleetRole)}
                                            </div>
                                        </div>
                                    </SidebarMenuButton>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    side="top"
                                    className="w-(--radix-popper-anchor-width)"
                                >
                                    <DropdownMenuItem
                                        className="cursor-pointer"
                                        onClick={() => navigate(routeSettings.path())}
                                    >
                                        <span>{t("component.userMenu.settings")}</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="cursor-pointer" onClick={logout}>
                                        <span>{t("component.userMenu.logout")}</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarFooter>
            </Sidebar>
            <Notifications open={notificationsOpen} onOpenChange={setNotificationsOpen} />
            <HelpCenter
                open={helpOpened}
                onOpenChange={setHelpOpened}
                suggestedHelpArticles={suggestedHelpArticles}
            />
        </>
    );
});
